"use client";

import * as Accordion from "@radix-ui/react-accordion";
import { motion, useInView } from "src/lib/motion";
import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

type AccordionItemProps = {
  children: React.ReactNode;
  className?: string;
} & Accordion.AccordionItemProps;

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Item
      className={cn(
        "mt-px overflow-hidden focus-within:relative focus-within:z-10",
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Accordion.Item>
  )
);
AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = {
  children: React.ReactNode;
  className?: string;
};

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Header className="flex">
      <Accordion.Trigger
        className={cn(
          "group flex flex-1 cursor-pointer items-center justify-between px-5 text-[15px] leading-none outline-none",
          className
        )}
        {...props}
        ref={forwardedRef}
      >
        {children}
      </Accordion.Trigger>
    </Accordion.Header>
  )
);
AccordionTrigger.displayName = "AccordionTrigger";
type AccordionContentProps = {
  children: ReactNode;
  className?: string;
} & Accordion.AccordionContentProps;

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Content
      className={cn(
        "overflow-hidden text-[15px] font-medium data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down",
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="px-5 py-2">{children}</div>
    </Accordion.Content>
  )
);
AccordionContent.displayName = "AccordionContent";

export type FeaturesDataProps = {
  id: number;
  title: string;
  content: string;
  image?: string;
  video?: string;
  icon?: React.ReactNode;
};

export type FeaturesProps = {
  collapseDelay?: number;
  ltr?: boolean;
  linePosition?: "left" | "right" | "top" | "bottom";
  data: FeaturesDataProps[];
};

export default function Features({
  collapseDelay = 5000,
  ltr = false,
  linePosition = "left",
  data = [],
}: FeaturesProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const carouselRef = useRef<HTMLUListElement>(null);
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    amount: 0.5,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      
      if (isInView) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(-1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isInView]);

  const scrollToIndex = (index: number) => {
    
    if (carouselRef.current && !isDragging) {
      const card = carouselRef.current.querySelectorAll(".card")[index];
      if (card) {
        const cardRect = card.getBoundingClientRect();
        const carouselRect = carouselRef.current.getBoundingClientRect();
        const offset =
          cardRect.left -
          carouselRect.left -
          (carouselRect.width - cardRect.width) / 2;
        

        carouselRef.current.scrollTo({
          left: carouselRef.current.scrollLeft + offset,
          behavior: "smooth",
        });
      }
    }
  };

  // Auto-scroll nur auf Desktop
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex !== undefined ? (prevIndex + 1) % data.length : 0
        );
      }, collapseDelay);

      return () => clearInterval(timer);
    }
  }, [collapseDelay, data.length]);

  // Auto-scroll nur auf Desktop
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      const handleAutoScroll = () => {
        const nextIndex =
          (currentIndex !== undefined ? currentIndex + 1 : 0) % data.length;
        scrollToIndex(nextIndex);
      };

      const autoScrollTimer = setInterval(handleAutoScroll, collapseDelay);

      return () => clearInterval(autoScrollTimer);
    }
  }, [collapseDelay, currentIndex, data.length]);

  // Touch handling
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    console.log('touch start');
    
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    
    if (!isDragging) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    setIsDragging(false);

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) < minSwipeDistance) {
      return;
    }
    
    if (distance > 0) {
      // Swipe left
      const newIndex = Math.min(data.length - 1, currentIndex + 1);
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
    }

    if (distance < 0) {
      // Swipe right
      const newIndex = Math.max(0, currentIndex - 1);
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      let scrollTimeout: NodeJS.Timeout;
      const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const scrollLeft = carousel.scrollLeft;
          const cardWidth = carousel.querySelector(".card")?.clientWidth || 0;
          const containerWidth = carousel.clientWidth;
          const scrollPosition = scrollLeft + containerWidth / 2;
          const newIndex = Math.min(
            Math.floor(scrollPosition / cardWidth),
            data.length - 1
          );
          setCurrentIndex(newIndex);
        }, 50);
      };

      carousel.addEventListener("scroll", handleScroll);
      return () => {
        clearTimeout(scrollTimeout);
        carousel.removeEventListener("scroll", handleScroll);
      };
    }
  }, [data.length]);

  return (
    <section ref={ref} id="features">
      <div>
        <div className="max-w-6xl mx-auto">
          <div className="mx-auto my-3 max-lg:mb-10 lg:my-12 lg:mb-20 h-full grid lg:grid-cols-2 gap-2 md:gap-10 items-center relative">
            <div
              className={` hidden lg:flex order-1 lg:order-[0] ${
                ltr ? "lg:order-2 lg:justify-end" : "justify-start"
              }`}
            >
              <Accordion.Root
                className=""
                type="single"
                defaultValue={`item-${currentIndex}`}
                value={`item-${currentIndex}`}
                onValueChange={(value) =>
                  setCurrentIndex(Number(value.split("-")[1]))
                }
              >
                {data.map((item, index) => (
                  <AccordionItem
                    key={item.id}
                    className="relative mb-8 last:mb-0"
                    value={`item-${index}`}
                  >
                    {linePosition === "left" || linePosition === "right" ? (
                      <div
                        className={`absolute bottom-0 top-0 h-full w-0.5 overflow-hidden rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 ${
                          linePosition === "right"
                            ? "left-auto right-0"
                            : "left-0 right-auto"
                        }`}
                      >
                        <div
                          className={`absolute left-0 top-0 w-full ${
                            currentIndex === index ? "h-full" : "h-0"
                          } origin-top bg-primary transition-all ease-linear dark:bg-white`}
                          style={{
                            transitionDuration:
                              currentIndex === index
                                ? `${collapseDelay}ms`
                                : "0s",
                          }}
                        ></div>
                      </div>
                    ) : null}

                    {linePosition === "top" || linePosition === "bottom" ? (
                      <div
                        className={`absolute left-0 right-0 w-full h-0.5 overflow-hidden rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 ${
                          linePosition === "bottom" ? "bottom-0" : "top-0"
                        }`}
                      >
                        <div
                          className={`absolute left-0 ${
                            linePosition === "bottom" ? "bottom-0" : "top-0"
                          } h-full ${
                            currentIndex === index ? "w-full" : "w-0"
                          } origin-left bg-primary transition-all ease-linear dark:bg-white`}
                          style={{
                            transitionDuration:
                              currentIndex === index
                                ? `${collapseDelay}ms`
                                : "0s",
                          }}
                        ></div>
                      </div>
                    ) : null}

                    <div className="flex items-center relative">
                      <div className="item-box w-12 h-12 bg-primary/10 rounded-full sm:mx-6 mx-2 shrink-0 flex items-center justify-center">
                        {item.icon}
                      </div>

                      <div>
                        <AccordionTrigger className="text-xl font-bold pl-0">
                          {item.title}
                        </AccordionTrigger>

                        <AccordionTrigger className="justify-start text-left leading-4 text-[16px] pl-0">
                          {item.content}
                        </AccordionTrigger>
                      </div>
                    </div>
                  </AccordionItem>
                ))}
              </Accordion.Root>
            </div>
            <div
              className={`h-[300px] md:h-[450px] min-h-[200px] w-auto  ${
                ltr && "lg:order-1"
              }`}
            >
              {data[currentIndex]?.image ? (
                <motion.img
                  key={currentIndex}
                  src={data[currentIndex].image}
                  alt="feature"
                  className="aspect-auto h-full w-full rounded-xl object-contain"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              ) : data[currentIndex]?.video ? (
                <video
                  preload="auto"
                  src={data[currentIndex].video}
                  className="aspect-auto h-full w-full rounded-lg object-cover"
                  autoPlay
                  loop
                  muted
                />
              ) : (
                <div className="aspect-auto h-full w-full rounded-xl bg-gray-200"></div>
              )}
            </div>

            <ul
              ref={carouselRef}
              className="relative flex h-full snap-x flex-nowrap overflow-x-auto py-6 snap-mandatory lg:hidden [&::-webkit-scrollbar]:hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                padding: "20px calc(15%)",
                scrollBehavior: "smooth",
                touchAction: "pan-x",
              }}
            >
              {data.map((item, index) => (
                <li
                  key={item.id}
                  className={`card relative mr-4 grid h-full lg:w-[280px] shrink-0 items-start justify-center rounded-lg bg-white/5 p-0 max-lg:pl-1 lg:p-6 transition-transform last:mr-0 dark:bg-black/5 ${
                    isDragging ? "cursor-grabbing" : "cursor-grab"
                  }`}
                  onClick={() => {
                    setCurrentIndex(index);
                    scrollToIndex(index);
                  }}
                  style={{
                    scrollSnapAlign: "center",
                    scrollSnapStop: "always",
                  }}
                >
                  <div className="max-lg:hidden absolute bottom-0 left-0 right-auto top-0 h-0.5 w-full overflow-hidden rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30">
                    <div
                      className={`absolute left-0 top-0 h-full ${
                        currentIndex === index ? "w-full" : "w-0"
                      } origin-top bg-primary transition-all duration-300 ease-out`}
                    ></div>
                  </div>
                  <h2 className="mb-3 text-lg font-bold">{item.title}</h2>
                  <p className="mx-0 max-w-sm lg:text-balance text-sm leading-relaxed text-muted-foreground">
                    {item.content}
                  </p>
                </li>
              ))}
            </ul>
            
            {/* Navigation Dots */}
            <div className="mt-4 flex justify-center space-x-2 lg:hidden [&>button]:relative [&>button]:z-[11]">
              {data.map((_, index) => (
                <button
                aria-label="dots"
                  key={index}
                  className={`h-2 w-2 rounded-full transition-all ${
                    currentIndex === index
                      ? "w-4 bg-primary"
                      : "bg-gray-300 dark:bg-gray-600"
                  }`}
                  onClick={() => {
                    setCurrentIndex(index);
                    scrollToIndex(index);
                    
                  }}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <div className="absolute inset-0 flex items-end -mb-3 justify-between lg:hidden">
              <button
              aria-label="prev"
                onClick={() => {
                  const newIndex = Math.max(0, currentIndex - 1);
                  setCurrentIndex(newIndex);
                  scrollToIndex(newIndex);
                }}
                className="border border-primary rounded-full p-2 z-10 duration-200 md:hover:bg-primary md:hover:text-white"
              >
                <svg
                  className="h-5 w-5 text-gray-800 dark:text-white"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
              aria-label="next"
                onClick={() => {
                  const newIndex = Math.min(data.length - 1, currentIndex + 1);
                  setCurrentIndex(newIndex);
                  scrollToIndex(newIndex);
                }}
                className="border border-primary rounded-full p-2 z-10 duration-200 md:hover:bg-primary md:hover:text-white"
              >
                <svg
                  className="h-5 w-5 text-gray-800 dark:text-white"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
